/**
 * Test utility functions for handling test scoring, passing thresholds, and calculations
 */
import { getActiveLcSettings } from './lc_settings'

/**
 * Get passing threshold based on test difficulty
 * @param {string} difficulty - Test difficulty level ('basic', 'intermediate', 'advanced', 'easy', 'medium', 'hard')
 * @returns {number} - Passing threshold as decimal (0.5 = 50%)
 */
export const getPassingThreshold = (difficulty) => {
  const difficultyMap = {
    'basic': 0.5,        // 50%
    'easy': 0.5,         // 50%
    'intermediate': 0.65, // 65%
    'medium': 0.65,       // 65%
    'advanced': 0.8,     // 80%
    'hard': 0.8          // 80%
  };
  
  const normalizedDifficulty = difficulty ? difficulty.toLowerCase() : 'basic';
  return difficultyMap[normalizedDifficulty] || 0.5; // Default to 50% if unknown
};

/**
 * Get minimum questions required for test difficulty
 * @param {string} difficulty - Test difficulty level
 * @returns {number} - Minimum number of questions required
 */
export const getMinimumQuestions = (difficulty) => {
  const minimumMap = {
    'basic': 4,
    'easy': 4,
    'intermediate': 8,
    'medium': 8,
    'advanced': 10,
    'hard': 10
  };
  
  const normalizedDifficulty = difficulty ? difficulty.toLowerCase() : 'basic';
  return minimumMap[normalizedDifficulty] || 4;
};

/**
 * Get maximum retry attempts for test difficulty
 * @param {string} difficulty - Test difficulty level
 * @returns {number} - Maximum retry attempts (0 = no retries)
 */
export const getMaxRetryAttempts = (difficulty) => {
  const retryMap = {
    'basic': 2,        // Can redo 2 times (3 total attempts)
    'easy': 2,         // Can redo 2 times (3 total attempts)
    'intermediate': 1, // Can redo 1 time (2 total attempts)
    'medium': 1,       // Can redo 1 time (2 total attempts)
    'advanced': 0,     // No retries (1 total attempt)
    'hard': 0          // No retries (1 total attempt)
  };
  
  const normalizedDifficulty = difficulty ? difficulty.toLowerCase() : 'basic';
  return retryMap[normalizedDifficulty] || 0;
};

/**
 * Get points per correct answer based on difficulty
 * @param {string} difficulty - Test difficulty level
 * @returns {number} - Points earned per correct answer
 */
export const getPointsPerAnswer = (difficulty) => {
  const normalizedDifficulty = difficulty ? String(difficulty).toLowerCase() : 'basic'
  const s = getActiveLcSettings()
  switch (normalizedDifficulty) {
    case 'basic':
    case 'easy':
      return s.basicAnswerLc
    case 'intermediate':
    case 'medium':
      return s.intermediateAnswerLc
    case 'advanced':
    case 'hard':
      return s.advancedAnswerLc
    default:
      return s.basicAnswerLc
  }
};

/**
 * Get test creation bonus multiplier based on difficulty
 * @param {string} difficulty - Test difficulty level
 * @returns {number} - Bonus multiplier (1.0 = no bonus, 1.1 = 10% bonus, 1.2 = 20% bonus)
 */
export const getCreationBonus = (difficulty) => {
  const bonusMap = {
    'basic': 1.0,      // No bonus
    'easy': 1.0,       // No bonus
    'intermediate': 1.1, // 10% bonus
    'medium': 1.1,     // 10% bonus
    'advanced': 1.2,   // 20% bonus
    'hard': 1.2        // 20% bonus
  };
  
  const normalizedDifficulty = difficulty ? difficulty.toLowerCase() : 'basic';
  return bonusMap[normalizedDifficulty] || 1.0;
};

/**
 * Calculate if a test result passes based on difficulty
 * @param {number} correctAnswers - Number of correct answers
 * @param {number} totalQuestions - Total number of questions
 * @param {string} difficulty - Test difficulty level
 * @returns {boolean} - Whether the test passes
 */
export const calculateTestPassed = (correctAnswers, totalQuestions, difficulty = 'basic') => {
  if (totalQuestions === 0) return false;
  
  const passRate = correctAnswers / totalQuestions;
  const threshold = getPassingThreshold(difficulty);
  
  return passRate >= threshold;
};

/**
 * Calculate LC points earned from a test
 * @param {number} correctAnswers - Number of correct answers
 * @param {boolean} passed - Whether the test was passed
 * @param {string} difficulty - Test difficulty level (default 'basic')
 * @param {number} maxLC - Maximum LC that can be earned (default 0.5)
 * @returns {number} - LC points earned
 */
export const calculateLCEarned = (correctAnswers, passed, difficulty = 'basic', maxLC = 0.5) => {
  if (!passed) return 0;
  
  const pointsPerAnswer = getPointsPerAnswer(difficulty);
  const safeCorrect = Math.max(0, Number(correctAnswers || 0));
  const earnedPoints = safeCorrect * pointsPerAnswer;
  // Ensure never negative and always finite
  const safe = Math.max(0, Math.min(Number.isFinite(earnedPoints) ? earnedPoints : 0, maxLC));
  return safe; // Cap at maximum and clamp to zero
};

/**
 * Get difficulty display information
 * @param {string} difficulty - Test difficulty level
 * @returns {object} - Object with display name and all difficulty parameters
 */
export const getDifficultyInfo = (difficulty) => {
  const threshold = getPassingThreshold(difficulty);
  const percentage = Math.round(threshold * 100);
  
  const displayNames = {
    'basic': 'Basic',
    'easy': 'Basic',
    'intermediate': 'Intermediate', 
    'medium': 'Intermediate',
    'advanced': 'Advanced',
    'hard': 'Advanced'
  };
  
  const normalizedDifficulty = difficulty ? difficulty.toLowerCase() : 'basic';
  const displayName = displayNames[normalizedDifficulty] || 'Basic';
  
  return {
    displayName,
    threshold: percentage,
    decimal: threshold,
    minimumQuestions: getMinimumQuestions(difficulty),
    maxRetryAttempts: getMaxRetryAttempts(difficulty),
    pointsPerAnswer: getPointsPerAnswer(difficulty),
    creationBonus: getCreationBonus(difficulty)
  };
};

/**
 * Validate if a test meets minimum question requirements for difficulty
 * @param {number} totalQuestions - Total number of questions in test
 * @param {string} difficulty - Test difficulty level
 * @returns {object} - Validation result with isValid boolean and message
 */
export const validateMinimumQuestions = (totalQuestions, difficulty) => {
  const minRequired = getMinimumQuestions(difficulty);
  const isValid = totalQuestions >= minRequired;
  
  return {
    isValid,
    minRequired,
    message: isValid 
      ? `✅ Test meets minimum requirement of ${minRequired} questions for ${getDifficultyInfo(difficulty).displayName} difficulty.`
      : `❌ ${getDifficultyInfo(difficulty).displayName} difficulty requires at least ${minRequired} questions. Current: ${totalQuestions}`
  };
}; 